// src/utils/ServiceVariationTransformer.ts
import { ServiceItem } from '../types/service';

export enum VariationType {
    HAIR = 'hair',
    SIZE = 'size',
    COMPLEXITY = 'complexity'
}

export enum ComplexityLevel {
    BASIC = 'Basic',
    STANDARD = 'Standard',
    COMPLEX = 'Complex',
    CUSTOM = 'Custom'
}

interface HairVariation {
    length: string;
    thickness: string;
    price: number;
    duration: number;
}

interface SizeVariation {
    size: string;
    price: number;
    duration: number;
}

interface ComplexityVariation {
    level: ComplexityLevel;
    price: number;
    duration: number;
}

export interface ServiceVariations {
    hair: HairVariation[];
    size: SizeVariation[];
    complexity: ComplexityVariation[];
}

export class VariationTransformer {
    private static readonly DEFAULT_LEVELS = [
        ComplexityLevel.BASIC,
        ComplexityLevel.STANDARD,
        ComplexityLevel.COMPLEX,
        ComplexityLevel.CUSTOM
    ];

    private static createEmptyVariations(): ServiceVariations {
        return {
            hair: [],
            size: [],
            complexity: []
        };
    }

    private static transformHairVariation(template: ServiceItem): HairVariation[] {
        if (!template.variations?.options?.lengths) return [];
        
        return template.variations.options.lengths.map(length => ({
            length,
            thickness: template.variations?.options?.thicknesses?.[0] || 'Medium',
            price: template.basePrice.min,
            duration: template.baseDuration.min
        }));
    }

    private static transformSizeVariation(template: ServiceItem): SizeVariation[] {
        if (!template.variations?.options?.sizes) return [];
        
        return template.variations.options.sizes.map(size => ({
            size,
            price: template.basePrice.min,
            duration: template.baseDuration.min
        }));
    }

    private static transformComplexityVariation(template: ServiceItem): ComplexityVariation[] {
        return this.DEFAULT_LEVELS.map(level => ({
            level,
            price: template.basePrice.min,
            duration: template.baseDuration.min
        }));
    }

    public static transform(template: ServiceItem): ServiceVariations {
        const variations = this.createEmptyVariations();

        if (!template.variations?.type) {
            return variations;
        }

        try {
            switch (template.variations.type) {
                case VariationType.HAIR:
                    variations.hair = this.transformHairVariation(template);
                    break;
                case VariationType.SIZE:
                    variations.size = this.transformSizeVariation(template);
                    break;
                case VariationType.COMPLEXITY:
                    variations.complexity = this.transformComplexityVariation(template);
                    break;
            }
        } catch (error) {
            console.error('Error transforming variations:', error);
        }

        return variations;
    }
}